/**
 * PollingManager - Centralized polling for WebRTC signaling
 *
 * Provides a single shared polling timer that emits events for:
 * - poll:answer - When an offer receives an answer
 * - poll:ice - When new ICE candidates are available
 *
 * Connections subscribe to these events and filter by offerId in their callbacks.
 */
import { EventEmitter } from 'eventemitter3';
/**
 * Centralized polling manager that emits global events
 * Connections subscribe to events and filter by offerId
 */
export class PollingManager extends EventEmitter {
    constructor(options) {
        super();
        this.pollingInterval = null;
        this.lastPollTimestamp = 0;
        this.running = false;
        this.api = options.api;
        this.intervalMs = options.intervalMs ?? PollingManager.DEFAULT_INTERVAL_MS;
        this.debugEnabled = options.debugEnabled ?? false;
    }
    /**
     * Start polling
     */
    start() {
        if (this.running) {
            this.debug('Already running');
            return;
        }
        this.debug('Starting polling manager');
        this.running = true;
        // Poll immediately
        this.poll();
        // Start interval
        this.pollingInterval = setInterval(() => {
            this.poll();
        }, this.intervalMs);
        this.emit('poll:started');
    }
    /**
     * Stop polling
     */
    stop() {
        if (!this.running)
            return;
        this.debug('Stopping polling manager');
        this.running = false;
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.emit('poll:stopped');
    }
    /**
     * Check if polling is active
     */
    isRunning() {
        return this.running;
    }
    /**
     * Get the last poll timestamp
     */
    getLastPollTimestamp() {
        return this.lastPollTimestamp;
    }
    /**
     * Perform a single poll
     */
    async poll() {
        if (!this.running)
            return;
        try {
            const result = await this.api.poll(this.lastPollTimestamp);
            // Emit answer events
            for (const answer of result.answers) {
                this.debug(`Poll: answer for ${answer.offerId}`);
                this.emit('poll:answer', {
                    offerId: answer.offerId,
                    answererPublicKey: answer.answererPublicKey,
                    sdp: answer.sdp,
                    answeredAt: answer.answeredAt,
                    matchedTags: answer.matchedTags,
                });
                // Update last poll timestamp
                if (answer.answeredAt > this.lastPollTimestamp) {
                    this.lastPollTimestamp = answer.answeredAt;
                }
            }
            // Emit ICE candidate events (grouped by offerId)
            for (const [offerId, candidates] of Object.entries(result.iceCandidates)) {
                if (candidates.length > 0) {
                    this.debug(`Poll: ${candidates.length} ICE candidates for ${offerId}`);
                    this.emit('poll:ice', {
                        offerId,
                        candidates: candidates,
                    });
                    // Update last poll timestamp from candidates
                    for (const candidate of candidates) {
                        if (candidate.createdAt > this.lastPollTimestamp) {
                            this.lastPollTimestamp = candidate.createdAt;
                        }
                    }
                }
            }
        }
        catch (error) {
            this.debug('Poll error:', error);
            this.emit('poll:error', error instanceof Error ? error : new Error(String(error)));
        }
    }
    /**
     * Debug logging
     */
    debug(...args) {
        if (this.debugEnabled) {
            console.log('[PollingManager]', ...args);
        }
    }
}
PollingManager.DEFAULT_INTERVAL_MS = 1000;
